import { useState, useEffect, useCallback, useRef } from "react";
import {
    Box,
    Typography,
    Tabs,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    Chip,
    Button,
    CircularProgress,
    useMediaQuery,
    useTheme,
    Tooltip,
} from "@mui/material";
import {
    CheckCircle,
    Close,
    MusicNote,
    Person,
    Message,
    AccessTime,
    RadioButtonUnchecked,
    TimerOff,
    YouTube,
    FilterList,
    Bolt as FlashIcon,
    Stop as StopIcon,
} from "@mui/icons-material"; 
// import { DatePicker } from "@mui/x-date-pickers/DatePicker";
// import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
// import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { timeSince, formatTimeAgo } from "../../utils/timeSince"; 
// import Header from "./Header"; 
import io from "socket.io-client";
import { useSnackbar } from 'notistack';

// Status colors and config
const statusConfig = {
    "Song-Q": {
        color: "#FFA726", // Orange
        icon: <AccessTime color="warning" />,
    },
    Played: {
        color: "#4CAF50", // Green
        icon: <CheckCircle color="success" />,
    },
    "Not Played": {
        color: "#F44336", // Red
        icon: <TimerOff color="error" />,
    },
};

// Action type colors
const actionTypeColors = {
    singForMe: "#2196F3", // Blue
    singWithYou: "#4CAF50", // Green
    singAlone: "#9C27B0", // Purple
};

const COOLDOWN_MS = 60 * 60 * 1000; // 1 hour

const AdminRequests = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const { enqueueSnackbar } = useSnackbar();
    const [selectedTab, setSelectedTab] = useState("Song-Q");
    const [requests, setRequests] = useState([]);
    const [selectedSong, setSelectedSong] = useState(null);
    const [openModal, setOpenModal] = useState(false);
    const [loading, setLoading] = useState(true);
    const [currentTime, setCurrentTime] = useState(Date.now());
    
    // Flash Quiz states
    const [activeFlashSession, setActiveFlashSession] = useState(null);
    const [loadingFlashSession, setLoadingFlashSession] = useState(false);
    // Delay now comes from Flash session config
    const [pushingQuestion, setPushingQuestion] = useState(false);
    const [stoppingQuestion, setStoppingQuestion] = useState(false);
    const [hasQuestionsAvailable, setHasQuestionsAvailable] = useState(false);
    const [checkingQuestions, setCheckingQuestions] = useState(false);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [pendingSongId, setPendingSongId] = useState(null);

    const socketRef = useRef(null);
    const isInitialMount = useRef(true);
    const [completedSongIds, setCompletedSongIds] = useState(() => new Set());
    const lastCurrentSongIdRef = useRef(null);
    const recentlyStoppedRef = useRef(false);

    // Load persisted "completed for session" state when session changes
    useEffect(() => {
        try {
            const sessionId = activeFlashSession?.session_id;
            if (!sessionId) {
                setCompletedSongIds(new Set());
                return;
            }
            const raw = localStorage.getItem(`flash_completed_${sessionId}`);
            if (raw) {
                const arr = JSON.parse(raw);
                if (Array.isArray(arr)) {
                    setCompletedSongIds(new Set(arr));
                }
            } else {
                setCompletedSongIds(new Set());
            }
        } catch {
            setCompletedSongIds(new Set());
        }
    }, [activeFlashSession?.session_id]);

    // Persist "completed for session" state whenever it changes
    useEffect(() => {
        try {
            const sessionId = activeFlashSession?.session_id;
            if (!sessionId) return;
            localStorage.setItem(`flash_completed_${sessionId}`, JSON.stringify(Array.from(completedSongIds)));
        } catch {}
    }, [completedSongIds, activeFlashSession?.session_id]);

    const getRequestStatus = useCallback((request) => {
        const now = new Date();
        const expiresAt = new Date(request.expires_at);
        if (request.status === "Fulfilled") return "Played";
        if (now >= expiresAt) return "Not Played";
        return "Song-Q";
    }, []);

    // Fetch active flash quiz session
    const fetchActiveFlashSession = useCallback(async () => {
        try {
            setLoadingFlashSession(true);
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/live-quiz/flash/active`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
                }
            });
            
            if (response.ok) {
                const data = await response.json();
                if (data.sessions && data.sessions.length > 0) {
                    setActiveFlashSession(data.sessions[0]);
                } else {
                    setActiveFlashSession(null);
                }
            }
        } catch (error) {
            console.error('Error fetching active flash session:', error);
        } finally {
            setLoadingFlashSession(false);
        }
    }, []);

    // Get current timer state
    const getTimerState = useCallback(() => {
        if (!activeFlashSession?.rapid_config) return null;
        
        try {
            const rapidConfig = typeof activeFlashSession.rapid_config === 'string' 
                ? JSON.parse(activeFlashSession.rapid_config) 
                : activeFlashSession.rapid_config;
            
            const pushedAt = rapidConfig.question_pushed_at;
            const deliveryTime = rapidConfig.question_delivery_time;
            const songTimerSeconds = rapidConfig.song_timer_seconds || 60;
            const currentSongId = rapidConfig.current_song_id;
            
            if (!pushedAt || !deliveryTime || !currentSongId) return null;
            
            const now = currentTime;
            const delivery = new Date(deliveryTime).getTime();
            const quizletEnd = delivery + (songTimerSeconds * 1000);
            
            // Check if we're in delay phase
            if (now < delivery) {
                const remaining = Math.max(0, Math.ceil((delivery - now) / 1000));
                return {
                    phase: 'delay',
                    remainingSeconds: remaining,
                    currentSongId,
                    songTimerSeconds
                };
            }
            
            // Check if we're in quizlet timer phase
            if (now < quizletEnd) {
                const remaining = Math.max(0, Math.ceil((quizletEnd - now) / 1000));
                return {
                    phase: 'quizlet',
                    remainingSeconds: remaining,
                    currentSongId,
                    songTimerSeconds
                };
            }
            
            // Timer ended
            return null;
        } catch {
            return null;
        }
    }, [activeFlashSession, currentTime]);

    // Format timer display
    const formatTimer = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // Push song question to flash quiz
    const pushFlashQuestion = async (songId) => {
        if (!activeFlashSession) {
            enqueueSnackbar('No active flash quiz session', { variant: 'warning' });
            return;
        }

        const timerState = getTimerState();
        
        // Check if another song is currently running
        if (timerState && timerState.currentSongId !== songId) {
            setPendingSongId(songId);
            setConfirmDialogOpen(true);
            return;
        }

        try {
            setPushingQuestion(true);
            const rawRapid = activeFlashSession?.rapid_config;
            const parsedRapid = typeof rawRapid === 'string' ? (() => { try { return JSON.parse(rawRapid); } catch { return {}; } })() : (rawRapid || {});
            const sessionDelay = typeof parsedRapid.delay_seconds === 'number' ? parsedRapid.delay_seconds : 10;
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/live-quiz/flash/sessions/${activeFlashSession.session_id}/push-song/${songId}`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
                    },
                    body: JSON.stringify({
                        delay_seconds: sessionDelay
                    })
                }
            );

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to push question');
            }

            await response.json();
            enqueueSnackbar(`Question pushed! Delay per config: ${sessionDelay}s`, { variant: 'success' });
            await fetchActiveFlashSession();
        } catch (error) {
            console.error('Error pushing flash question:', error);
            enqueueSnackbar(error.message || 'Failed to push question', { variant: 'error' });
        } finally {
            setPushingQuestion(false);
            setPendingSongId(null);
            setConfirmDialogOpen(false);
        }
    };

    // Handle confirmation dialog proceed
    const handleConfirmProceed = async () => {
        if (pendingSongId) {
            await pushFlashQuestion(pendingSongId);
        }
    };

    // Stop current question
    const handleStopQuestion = async () => {
        if (!activeFlashSession) {
            enqueueSnackbar('No active flash quiz session', { variant: 'warning' });
            return;
        }

        try {
            // Treat this end as a manual stop so re-push is allowed
            recentlyStoppedRef.current = true;
            setStoppingQuestion(true);
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/live-quiz/flash/sessions/${activeFlashSession.session_id}/stop-question`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
                    }
                }
            );

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to stop question');
            }

            enqueueSnackbar('Question stopped successfully', { variant: 'success' });
            // Refresh session to get updated state
            await fetchActiveFlashSession();
        } catch (error) {
            console.error('Error stopping question:', error);
            enqueueSnackbar(error.message || 'Failed to stop question', { variant: 'error' });
        } finally {
            setStoppingQuestion(false);
        }
    };

    // Get last pushed time from session
    const getLastPushedTime = () => {
        if (!activeFlashSession?.rapid_config) return null;
        try {
            const rapidConfig = typeof activeFlashSession.rapid_config === 'string' 
                ? JSON.parse(activeFlashSession.rapid_config) 
                : activeFlashSession.rapid_config;
            return rapidConfig.question_pushed_at || null;
        } catch {
            return null;
        }
    };

    const fetchRequests = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/song-requests`,
                {
                    headers: {
                        'Cache-Control': 'no-cache'
                    }
                }
            );
            const data = await response.json();

            if (response.ok) {
                const requestsWithSongs = await Promise.all(
                    data.map(async (req) => {
                        try {
                            const songRes = await fetch(
                                `${process.env.REACT_APP_API_URL}/api/songs/${req.songs_master_id}`
                            );
                            const songData = await songRes.json();

                            // Fetch URLs and files count
                            const urlsRes = await fetch(
                                `${process.env.REACT_APP_API_URL}/api/song-urls/urls?songs_master_id=${req.songs_master_id}`
                            );
                            const urlsData = await urlsRes.json();

                            const filesRes = await fetch(
                                `${process.env.REACT_APP_API_URL}/api/song-files/song/${req.songs_master_id}`
                            );
                            const filesData = await filesRes.json();

                            // Filter for YouTube URLs specifically
                            const youtubeLinks = urlsData.filter((url) =>
                                url.song_url && (
                                    url.song_url.includes('youtube.com') ||
                                    url.song_url.includes('youtu.be') ||
                                    (url.url_type_name && url.url_type_name.toLowerCase().includes('youtube'))
                                ));

                            const validFiles = filesData.filter((file) => file.file);

                            return {
                                ...req,
                                song_name: songData.name || "Unknown Song",
                                artist: songData.artist || "Unknown Artist",
                                displayStatus: getRequestStatus(req),
                                hasYoutubeLinks: youtubeLinks.length > 0,
                                youtubeCount: youtubeLinks.length,
                                hasFiles: validFiles.length > 0,
                                fileCount: validFiles.length,
                            };
                        } catch {
                            return {
                                ...req,
                                song_name: "Unknown Song",
                                artist: "Unknown Artist",
                                displayStatus: getRequestStatus(req),
                                hasYoutubeLinks: false,
                                youtubeCount: 0,
                                hasFiles: false,
                                fileCount: 0,
                            };
                        }
                    })
                );

                setRequests(requestsWithSongs);
            }
        } catch (error) {
            console.error("Error fetching requests:", error);
        } finally {
            setLoading(false);
        }
    }, [getRequestStatus]);

    // Check if questions are available for the selected song
    const checkQuestionAvailability = useCallback(async (songId) => {
        if (!songId || !activeFlashSession) {
            setHasQuestionsAvailable(false);
            return;
        }

        try {
            setCheckingQuestions(true);
            const quizConfig = typeof activeFlashSession.quiz_config === 'string' 
                ? JSON.parse(activeFlashSession.quiz_config) 
                : activeFlashSession.quiz_config;
            
            const rapidConfig = typeof activeFlashSession.rapid_config === 'string' 
                ? JSON.parse(activeFlashSession.rapid_config) 
                : activeFlashSession.rapid_config;
            
            const difficultyId = quizConfig?.difficulty_id === 'all' ? 'all' : quizConfig?.difficulty_id;
            const questionsPerSong = quizConfig?.questions_per_song || rapidConfig?.questions_per_song || 3;
            
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/quiz/song/${songId}?` +
                `difficulty_id=${difficultyId || 'all'}&` +
                `questions_per_song=${questionsPerSong}`,
                {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
                    }
                }
            );
            
            if (response.ok) {
                const data = await response.json();
                const hasQuestions = data.questions && data.questions.length > 0;
                setHasQuestionsAvailable(hasQuestions);
            } else {
                setHasQuestionsAvailable(false);
            }
        } catch (error) {
            console.error('Error checking question availability:', error);
            setHasQuestionsAvailable(false);
        } finally {
            setCheckingQuestions(false);
        }
    }, [activeFlashSession]);

    // Fetch active flash session when modal opens; subsequent updates come via WebSocket events
    useEffect(() => {
        if (openModal) {
            fetchActiveFlashSession();
        }
    }, [openModal, fetchActiveFlashSession]);

    // Update current time every second for timer display
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(Date.now());
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    // Track when a song finishes its timer to disable re-push for that song
    useEffect(() => {
        const timerState = getTimerState();
        const previousSongId = lastCurrentSongIdRef.current;
        if (timerState && timerState.currentSongId) {
            lastCurrentSongIdRef.current = timerState.currentSongId;
        } else {
            if (previousSongId && !recentlyStoppedRef.current) {
                setCompletedSongIds(prev => {
                    const next = new Set(prev);
                    next.add(previousSongId);
                    return next;
                });
            }
            recentlyStoppedRef.current = false;
            lastCurrentSongIdRef.current = null;
        }
    }, [getTimerState]);

    // Check question availability when song is selected and session is available
    useEffect(() => {
        if (openModal && selectedSong?.songs_master_id && activeFlashSession) {
            checkQuestionAvailability(selectedSong.songs_master_id);
        } else {
            setHasQuestionsAvailable(false);
        }
    }, [openModal, selectedSong?.songs_master_id, activeFlashSession, checkQuestionAvailability]);

    // Auto-close confirmation dialog when timer ends
    useEffect(() => {
        const timerState = getTimerState();
        if (!timerState && confirmDialogOpen) {
            setConfirmDialogOpen(false);
            setPendingSongId(null);
        }
    }, [getTimerState, confirmDialogOpen]);

    // Get button state for the selected song
    const getButtonState = useCallback(() => {
        if (!selectedSong) return { type: 'push', disabled: false };
        
        const timerState = getTimerState();
        const isCurrentSong = timerState && timerState.currentSongId === selectedSong.songs_master_id;
        const isCompleted = completedSongIds.has(selectedSong.songs_master_id);
        
        if (!timerState) {
            return { type: 'push', disabled: isCompleted };
        }
        
        if (isCurrentSong) {
            return {
                type: 'stop',
                color: timerState.phase === 'delay' ? 'success' : 'error', // green for delay, red for quizlet
                disabled: stoppingQuestion,
                timer: formatTimer(timerState.remainingSeconds),
                phase: timerState.phase
            };
        }
        
        return { type: 'push', disabled: isCompleted };
    }, [selectedSong, getTimerState, stoppingQuestion, completedSongIds]);

    useEffect(() => {
        // Initialize socket connection only once
        if (!socketRef.current) {
            socketRef.current = io(process.env.REACT_APP_API_URL, {
                reconnectionAttempts: 5,
                reconnectionDelay: 1000,
            });

            socketRef.current.on("new-song-request", (newRequest) => {
                setRequests(prev => {
                    const exists = prev.some(req => req.song_request_id === newRequest.song_request_id);
                    if (!exists) {
                        return [{
                            ...newRequest,
                            displayStatus: getRequestStatus(newRequest)
                        }, ...prev];
                    }
                    return prev;
                });
            });

            socketRef.current.on("song-request-updated", (updatedRequest) => {
                setRequests(prev => prev.map(req =>
                    req.song_request_id === updatedRequest.song_request_id ? {
                        ...updatedRequest,
                        displayStatus: getRequestStatus(updatedRequest)
                    } : req
                ));
            });

            // Join global flash updates
            socketRef.current.emit('flash:join-global');

            // When a question is pushed via any admin, stop loading state and refresh session
            socketRef.current.on('flash:question_pushed', async (payload) => {
                try {
                    setPushingQuestion(false);
                    await fetchActiveFlashSession();
                } catch {}
            });

            // When a question is stopped, also clear stop loading state and refresh
            socketRef.current.on('flash:question_stopped', async (payload) => {
                try {
                    setStoppingQuestion(false);
                    await fetchActiveFlashSession();
                } catch {}
            });

            // Clear completed state when a session ends
            socketRef.current.on('flash:session_ended', async (payload) => {
                try {
                    if (payload?.session_id) {
                        localStorage.removeItem(`flash_completed_${payload.session_id}`);
                    }
                } catch {}
                setCompletedSongIds(new Set());
            });
        }

        // Initial data fetch
        if (isInitialMount.current) {
            fetchRequests();
            isInitialMount.current = false;
        }

        // Update current time every minute for expiration checks
        const timeInterval = setInterval(() => {
            setCurrentTime(Date.now());
        }, 60000);

        // Update request statuses periodically (every 5 minutes)
        const statusUpdateInterval = setInterval(() => {
            setRequests(prev => prev.map(req => ({
                ...req,
                displayStatus: getRequestStatus(req)
            })));
        }, 5 * 60 * 1000);

        return () => {
            clearInterval(timeInterval);
            clearInterval(statusUpdateInterval);
            if (socketRef.current) {
                socketRef.current.disconnect();
                socketRef.current = null;
            }
        };
    }, [fetchRequests, getRequestStatus]);

    const handleStatusChange = async (requestId, songMasterId, currentStatus, actionType) => {
        const newStatus = currentStatus === "Song-Q" ? "Played" : "Song-Q";
        
        let requestsToUpdate = [];
        
        if (actionType === "singForMe") {
            // Batch update: Get all singForMe requests for this song
            requestsToUpdate = requests.filter(req => 
                req.songs_master_id === songMasterId && req.action_type === "singForMe"
            );
        } else {
            // Individual update: Only update the specific request
            requestsToUpdate = requests.filter(req => req.song_request_id === requestId);
        }
        
        try {
            // Update all relevant requests
            await Promise.all(requestsToUpdate.map(async (req) => {
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/api/song-requests/${req.song_request_id}/song/${songMasterId}`,
                    {
                        method: "PATCH",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                            status: newStatus === "Played" ? "Fulfilled" : "Pending"
                        }),
                    }
                );

                if (!response.ok) {
                    throw new Error(await response.text());
                }

                return response.json();
            }));

            // Update local state for all relevant requests
            setRequests(prev => prev.map(req => {
                const shouldUpdate = requestsToUpdate.some(r => r.song_request_id === req.song_request_id);
                return shouldUpdate ? {
                    ...req,
                    status: newStatus === "Played" ? "Fulfilled" : "Pending",
                    displayStatus: newStatus,
                    fulfilled_at: newStatus === "Played" ? new Date().toISOString() : null
                } : req;
            }));

            // Notify other clients via socket for all updated requests
            if (socketRef.current) {
                requestsToUpdate.forEach(req => {
                    socketRef.current.emit("song-request-updated", {
                        ...req,
                        status: newStatus === "Played" ? "Fulfilled" : "Pending",
                        displayStatus: newStatus,
                        fulfilled_at: newStatus === "Played" ? new Date().toISOString() : null
                    });
                });
            }

        } catch (error) {
            console.error("Error updating status:", error);
        }
    };

    const handleOpenDetails = async (songId, request) => {
        try {
            const songRes = await fetch(
                `${process.env.REACT_APP_API_URL}/api/songs/${songId}`
            );
            const songData = await songRes.json();

            const urlsRes = await fetch(
                `${process.env.REACT_APP_API_URL}/api/song-urls/urls?songs_master_id=${songId}`
            );
            const urlsData = await urlsRes.json();

            const filesRes = await fetch(
                `${process.env.REACT_APP_API_URL}/api/song-files/song/${songId}`
            );
            const filesData = await filesRes.json();

            // Get requests for this song based on current tab
            let songRequests;
            if (selectedTab === "Song-Q") {
                // Only show current Song-Q requests
                songRequests = requests.filter(req => 
                    req.songs_master_id === songId && req.displayStatus === "Song-Q"
                );
            } else {
                // Show all requests for this song (including past ones)
                songRequests = requests.filter(req => req.songs_master_id === songId);
            }

            setSelectedSong({
                ...songData,
                request: {
                    requests: songRequests,
                    ...request
                },
                urls: urlsData,
                files: filesData
            });
            setOpenModal(true);
        } catch (error) {
            console.error("Error loading song info:", error);
        }
    };

    // Filter requests by selected tab and date range
    const getFilteredRequests = () => {
        let filtered = requests.filter((req) => {
            // Tab-based filtering
            if (selectedTab === "Song-Q") {
                // Only show current Song-Q requests (no past requests)
                return req.displayStatus === "Song-Q";
            } else {
                // For Played/Not Played tabs, show all requests including past ones
                return selectedTab === "All" || req.displayStatus === selectedTab;
            }
        });

        return filtered;
    };

    const filteredRequests = getFilteredRequests();

    // Sort by most recent first (individual requests)
    const sortedRequests = [...filteredRequests].sort((a, b) =>
        new Date(b.created_at) - new Date(a.created_at)
    );

    // Calculate request counts for each song by action type
    const getRequestCountForSong = (songId, actionType) => {
        let baseRequests;
        
        if (selectedTab === "Song-Q") {
            // Only count current Song-Q requests
            baseRequests = requests.filter(req => 
                req.songs_master_id === songId && 
                req.action_type === actionType &&
                req.displayStatus === "Song-Q"
            );
        } else {
            // Count all requests for this song and action type
            baseRequests = requests.filter(req => 
                req.songs_master_id === songId && 
                req.action_type === actionType
            );
        }
        
        return baseRequests.length;
    };

    return (
        // <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ p: isMobile ? 1 : 3, bgcolor: "#121212", minHeight: "100vh" }}>
                <Typography variant="h5" gutterBottom sx={{ color: "white", mb: 2 }}>
                    Song Requests
                </Typography>

                <Tabs
                    value={selectedTab}
                    onChange={(e, newValue) => setSelectedTab(newValue)}
                    sx={{
                        mb: 2,
                        "& .MuiTabs-indicator": {
                            backgroundColor: statusConfig[selectedTab]?.color || "#1db954",
                        }
                    }}
                >
                    <Tab label="Song-Q" value="Song-Q" sx={{ color: "white" }} />
                    <Tab label="Not Played" value="Not Played" sx={{ color: "white" }} />
                    <Tab label="Played" value="Played" sx={{ color: "white" }} />
                </Tabs>

                {/* Date Range Filter for Played/Not Played tabs */}
                {/* {shouldShowFilters && (
                    <Box sx={{ mb: 2 }}>
                        <Button
                            startIcon={<FilterList />}
                            onClick={() => setShowFilters(!showFilters)}
                            sx={{ color: "white", mb: 1 }}
                        >
                            {showFilters ? "Hide Filters" : "Show Filters"}
                        </Button>
                        
                        {showFilters && (
                            <Stack direction={isMobile ? "column" : "row"} spacing={2} sx={{ mt: 2 }}>
                                <DatePicker
                                    label="Start Date"
                                    value={startDate}
                                    onChange={setStartDate}
                                    renderInput={(params) => (
                                        <TextField 
                                            {...params} 
                                            size="small"
                                            sx={{ 
                                                '& .MuiOutlinedInput-root': { color: 'white' },
                                                '& .MuiInputLabel-root': { color: 'white' },
                                                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'gray' },
                                            }}
                                        />
                                    )}
                                />
                                <DatePicker
                                    label="End Date"
                                    value={endDate}
                                    onChange={setEndDate}
                                    renderInput={(params) => (
                                        <TextField 
                                            {...params} 
                                            size="small"
                                            sx={{ 
                                                '& .MuiOutlinedInput-root': { color: 'white' },
                                                '& .MuiInputLabel-root': { color: 'white' },
                                                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'gray' },
                                            }}
                                        />
                                    )}
                                />
                                <Button 
                                    onClick={resetFilters}
                                    variant="outlined"
                                    sx={{ color: "white", borderColor: "gray" }}
                                >
                                    Reset
                                </Button>
                            </Stack>
                        )}
                    </Box>
                )} */}

                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : sortedRequests.length === 0 ? (
                    <Typography sx={{ color: "white", textAlign: "center", mt: 4 }}>
                        No requests found
                    </Typography>
                ) : (
                    <TableContainer component={Paper} sx={{ bgcolor: "#1e1e1e" }}>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: "#2c2c2c" }}>
                                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>Action</TableCell>
                                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>Requester Name</TableCell>
                                    <TableCell sx={{ color: "white", fontWeight: "bold", paddingLeft: "20px", minWidth: isMobile ? "200px" : "none" }}>Song</TableCell>
                                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>Action Type</TableCell>
                                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>Requests</TableCell>
                                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sortedRequests.map((request, index) => {
                                    const timePassed = currentTime - new Date(request.created_at).getTime();
                                    const timeLeft = COOLDOWN_MS - timePassed;
                                    const isExpiringSoon = timeLeft > 0 && timeLeft < 30 * 60 * 1000;
                                    const requestCount = getRequestCountForSong(request.songs_master_id, request.action_type);

                                    return (
                                        <TableRow
                                            key={request.song_request_id}
                                            hover
                                            onClick={() => handleOpenDetails(request.songs_master_id, request)}
                                            sx={{
                                                cursor: "pointer",
                                                "&:hover": { bgcolor: "#282828" }
                                            }}
                                        >
                                            <TableCell>
                                                <IconButton
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleStatusChange(
                                                            request.song_request_id,
                                                            request.songs_master_id,
                                                            request.displayStatus,
                                                            request.action_type
                                                        );
                                                    }}
                                                    size="small"
                                                    sx={{ color: statusConfig[request.displayStatus]?.color }}
                                                >
                                                    {request.displayStatus === "Played" ? (
                                                        <CheckCircle />
                                                    ) : (
                                                        <RadioButtonUnchecked />
                                                    )}
                                                </IconButton>
                                            </TableCell>
                                            <TableCell sx={{ color: "white" }}>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    <Person sx={{ fontSize: 20 }} />
                                                    <Typography>
                                                        {request.requester_name || "Anonymous"}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ color: "white", maxWidth: isMobile ? "300px" : "none" }}>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    <MusicNote sx={{ fontSize: 20 }} />
                                                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                                                        {request.song_name} <br />
                                                        <Typography variant="caption" color="#9e9e9eff">
                                                            {request.artist}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={request.action_type || "N/A"}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: actionTypeColors[request.action_type] || "#9e9e9e",
                                                        color: "white"
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={requestCount}
                                                    color="primary"
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    <Chip
                                                        label={request.displayStatus}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: statusConfig[request.displayStatus]?.color || "#9e9e9e",
                                                            color: "white"
                                                        }}
                                                    />
                                                    {request.displayStatus === "Song-Q" && timeLeft > 0 && (
                                                        <Tooltip title={`Expires in ${Math.ceil(timeLeft / (60 * 1000))} minutes`}>
                                                            <Chip
                                                                label={`${Math.ceil(timeLeft / (60 * 1000))}m`}
                                                                size="small"
                                                                color={isExpiringSoon ? "warning" : "default"}
                                                                variant="outlined"
                                                            />
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                {/* Details Dialog */}
                <Dialog
                    open={openModal}
                    onClose={() => setOpenModal(false)}
                    maxWidth="md"
                    fullWidth
                    PaperProps={{
                        sx: {
                            bgcolor: "#1e1e1e",
                            color: "white",
                        },
                    }}
                >
                    <DialogTitle sx={{ bgcolor: "#2c2c2c", display: "flex", justifyContent: "space-between" }}>
                        <Typography variant="h6">
                            {selectedSong?.name} {selectedSong?.release_year && `(${selectedSong.release_year})`}
                        </Typography>
                        <IconButton onClick={() => setOpenModal(false)} sx={{ color: "white" }}>
                            <Close />
                        </IconButton>
                    </DialogTitle>

                    <DialogContent dividers sx={{ bgcolor: "#121212" }}>
                        <Box mb={2}>
                            <Typography variant="subtitle1" fontWeight="bold">
                                Artist: <Typography component="span" color="text.secondary">{selectedSong?.artist || "N/A"}</Typography>
                            </Typography>
                        </Box>

                        <Divider sx={{ borderColor: "#333", my: 2 }} />

                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            Requests ({selectedSong?.request?.requests?.length || 1})
                            {selectedTab === "Song-Q" && (
                                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                    (Current requests only)
                                </Typography>
                            )}
                        </Typography>

                        {selectedSong?.request?.requests?.map((req, i) => (
                            <Box key={i} sx={{ mb: 2, p: 2, bgcolor: "#1a1a1a", borderRadius: 1 }}>
                                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <Person fontSize="small" />
                                        <Typography>{req.requester_name}</Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", gap: 1 }}>
                                        <Chip
                                            label={req.action_type || "N/A"}
                                            size="small"
                                            sx={{
                                                bgcolor: actionTypeColors[req.action_type] || "#9e9e9e",
                                                color: "white"
                                            }}
                                        />
                                        <Chip
                                            label={req.displayStatus}
                                            size="small"
                                            sx={{
                                                bgcolor: statusConfig[req.displayStatus]?.color || "#9e9e9e",
                                                color: "white"
                                            }}
                                        />
                                    </Box>
                                </Box>

                                {req.dedication_msg && (
                                    <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                                        <Message fontSize="small" />
                                        <Typography sx={{ fontStyle: "italic" }}>
                                            "{req.dedication_msg}"
                                        </Typography>
                                    </Box>
                                )}

                                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                    <Typography variant="caption" color="text.secondary">
                                        Requested: {timeSince(req.created_at)}
                                    </Typography>
                                    {req.fulfilled_at && (
                                        <Typography variant="caption" color="#4caf50">
                                            Fulfilled: {timeSince(req.fulfilled_at)}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        ))}

                        <Divider sx={{ borderColor: "#333", my: 2 }} />

                        {/* Flash Quiz Push Button */}
                        <Box sx={{ mb: 3, p: 2, bgcolor: "#1a1a1a", borderRadius: 1, border: "1px solid #9C27B0" }}>
                            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                                <FlashIcon sx={{ color: "#9C27B0", mr: 1 }} />
                                <Typography variant="subtitle1" fontWeight="bold">
                                    Flash Quiz
                                </Typography>
                            </Box>
                            
                            {loadingFlashSession ? (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <CircularProgress size={20} sx={{ color: "#9C27B0" }} />
                                    <Typography variant="body2" color="text.secondary">
                                        Loading...
                                    </Typography>
                                </Box>
                            ) : activeFlashSession ? (
                                <Box>
                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                            Active Session: <strong>{activeFlashSession.quiz_name}</strong>
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Participants: {activeFlashSession.participant_count || 0}
                                        </Typography>
                                        {getLastPushedTime() && (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                                Last pushed: {new Date(getLastPushedTime()).toLocaleString()} ({formatTimeAgo(getLastPushedTime())})
                                            </Typography>
                                        )}
                                    </Box>
                                    
                                    {checkingQuestions ? (
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                                            <CircularProgress size={20} sx={{ color: "#9C27B0" }} />
                                            <Typography variant="body2" color="text.secondary">
                                                Checking question availability...
                                            </Typography>
                                        </Box>
                                    ) : hasQuestionsAvailable ? (
                                        <Box>
                                            {(() => {
                                                const buttonState = getButtonState();
                                                return (
                                                    <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                                                        {/* Timer Display */}
                                                        {buttonState.type === 'stop' && buttonState.timer && (
                                                            <Typography 
                                                                variant="body1" 
                                                                sx={{ 
                                                                    color: buttonState.phase === 'delay' ? '#4caf50' : '#f44336',
                                                                    fontWeight: 'bold',
                                                                    minWidth: '60px'
                                                                }}
                                                            >
                                                                {buttonState.timer}
                                                            </Typography>
                                                        )}
                                                        
                                                        {/* Single Push/Stop Button */}
                                                        <Button
                                                            variant={buttonState.type === 'push' ? 'contained' : 'outlined'}
                                                            startIcon={
                                                                buttonState.type === 'push' 
                                                                    ? (pushingQuestion ? <CircularProgress size={16} /> : <FlashIcon />)
                                                                    : (stoppingQuestion ? <CircularProgress size={16} /> : <StopIcon />)
                                                            }
                                                            onClick={() => {
                                                                if (buttonState.type === 'push') {
                                                                    pushFlashQuestion(selectedSong?.songs_master_id);
                                                                } else {
                                                                    handleStopQuestion();
                                                                }
                                                            }}
                                                            disabled={buttonState.disabled || pushingQuestion}
                                                            sx={{
                                                                ...(buttonState.type === 'push' ? {
                                                                    bgcolor: "#9C27B0",
                                                                    "&:hover": { bgcolor: "#7B1FA2" },
                                                                } : {
                                                                    borderColor: buttonState.color === 'success' ? '#4caf50' : '#f44336',
                                                                    color: buttonState.color === 'success' ? '#4caf50' : '#f44336',
                                                                    "&:hover": { 
                                                                        borderColor: buttonState.color === 'success' ? '#45a049' : '#d32f2f',
                                                                        bgcolor: buttonState.color === 'success' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)'
                                                                    },
                                                                }),
                                                                textTransform: "none",
                                                                fontWeight: 600
                                                            }}
                                                        >
                                                            {buttonState.type === 'push' 
                                                                ? (pushingQuestion ? 'Pushing...' : 'Push')
                                                                : 'Stop'
                                                            }
                                                        </Button>
                                                        
                                                        <Tooltip title="Refresh session">
                                                            <IconButton
                                                                onClick={fetchActiveFlashSession}
                                                                size="small"
                                                                sx={{ color: "#9C27B0" }}
                                                            >
                                                                <FilterList />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                );
                                            })()}
                                            
                                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                                                Delay is controlled by session config
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Box sx={{ p: 2, bgcolor: "#2c2c2c", borderRadius: 1, border: "1px solid #f44336" }}>
                                            <Typography variant="body2" color="#f44336" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                <TimerOff sx={{ fontSize: 20 }} />
                                                No question available for this song according to the current quiz configuration.
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    No active flash quiz session. Create one from the Quiz Dashboard.
                                </Typography>
                            )}
                        </Box>

                        <Divider sx={{ borderColor: "#333", my: 2 }} />

                        {/* YouTube Links */}
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                YouTube Links ({selectedSong?.urls?.length || 0})
                            </Typography>
                            {selectedSong?.urls?.length > 0 ? (
                                selectedSong.urls.map((url, i) => (
                                    <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                                        <YouTube sx={{ color: "red" }} />
                                        <a
                                            href={url.song_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ color: "#1db954", wordBreak: "break-all" }}
                                        >
                                            {url.url_type_name || "YouTube Link"}
                                        </a>
                                    </Box>
                                ))
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    No YouTube links available
                                </Typography>
                            )}
                        </Box>

                        {/* Song Files */}
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                Song Files ({selectedSong?.files?.length || 0})
                            </Typography>
                            {selectedSong?.files?.length > 0 ? (
                                selectedSong.files.map((file, i) => (
                                    <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                                        <MusicNote sx={{ color: "skyblue" }} />
                                        <a
                                            href={`${process.env.REACT_APP_API_URL}/${file.file}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ color: "#1db954", wordBreak: "break-all" }}
                                        >
                                            {file.file_type_name || file.file.split("\\").pop()}
                                        </a>
                                    </Box>
                                ))
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    No song files available
                                </Typography>
                            )}
                        </Box>
                    </DialogContent>

                    <DialogActions sx={{ bgcolor: "#2c2c2c" }}>
                        <Button
                            onClick={() => setOpenModal(false)}
                            variant="contained"
                            sx={{ bgcolor: "#1db954", "&:hover": { bgcolor: "#1ed760" } }}
                        >
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Confirmation Dialog */}
                <Dialog
                    open={confirmDialogOpen}
                    onClose={() => {
                        setConfirmDialogOpen(false);
                        setPendingSongId(null);
                    }}
                    PaperProps={{
                        sx: {
                            bgcolor: "#1e1e1e",
                            color: "white",
                            borderRadius: '12px',
                            minWidth: '500px',
                            maxWidth: '600px'
                        }
                    }}
                >
                    <DialogTitle sx={{ pb: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Confirm Push Question
                        </Typography>
                    </DialogTitle>
                    <DialogContent>
                        {(() => {
                            // Find the song to push from requests
                            const songToPush = requests.find(req => req.songs_master_id === pendingSongId);
                            const timerState = getTimerState();
                            
                            return (
                                <>
                                    <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', mb: 2 }}>
                                        {/* Song Card */}
                                        <Box sx={{ flex: 1 }}>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    gap: 2,
                                                    p: 2,
                                                    bgcolor: '#2a2a2a',
                                                    borderRadius: '8px',
                                                    border: '1px solid #333'
                                                }}
                                            >
                                                <img
                                                    src={songToPush?.image || songToPush?.song_image || "https://placehold.co/80x80"}
                                                    alt={songToPush?.song_name || 'Song'}
                                                    style={{
                                                        width: 80,
                                                        height: 80,
                                                        borderRadius: 8,
                                                        objectFit: 'cover'
                                                    }}
                                                />
                                                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5, color: '#fff' }}>
                                                        {songToPush?.song_name || songToPush?.name || 'Unknown Song'}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ color: '#999' }}>
                                                        {songToPush?.artist || 'Unknown Artist'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>

                                        {/* Timer Display */}
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '120px' }}>
                                            {timerState && (
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        p: 2,
                                                        bgcolor: timerState.phase === 'delay' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)',
                                                        borderRadius: '8px',
                                                        border: `1px solid ${timerState.phase === 'delay' ? '#4caf50' : '#f44336'}`,
                                                        minWidth: '100px'
                                                    }}
                                                >
                                                    <Typography variant="caption" sx={{ color: '#999', mb: 0.5 }}>
                                                        {timerState.phase === 'delay' ? 'Delay' : 'Quiz Timer'}
                                                    </Typography>
                                                    <Typography
                                                        variant="h5"
                                                        sx={{
                                                            fontWeight: 'bold',
                                                            color: timerState.phase === 'delay' ? '#4caf50' : '#f44336',
                                                            fontFamily: 'monospace'
                                                        }}
                                                    >
                                                        {formatTimer(timerState.remainingSeconds)}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>

                                    <Typography variant="body2" sx={{ color: '#999', mt: 2 }}>
                                        {(() => {
                                            const requestSong = requests.find(req => req.songs_master_id === timerState?.currentSongId);
                                            const runningSongName = requestSong ? requestSong.song_name : 'Another song';
                                            const currentSongName = songToPush?.song_name || songToPush?.name || 'this song';
                                            
                                            return `${runningSongName} is currently running. Do you want to stop it and push ${currentSongName}?`;
                                        })()}
                                    </Typography>
                                </>
                            );
                        })()}
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                        <Button
                            onClick={() => {
                                setConfirmDialogOpen(false);
                                setPendingSongId(null);
                            }}
                            variant="outlined"
                            sx={{
                                color: '#fff',
                                borderColor: '#666',
                                '&:hover': {
                                    borderColor: '#888',
                                    bgcolor: 'rgba(255, 255, 255, 0.05)'
                                }
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleConfirmProceed}
                            variant="contained"
                            disabled={pushingQuestion}
                            startIcon={pushingQuestion ? <CircularProgress size={16} /> : null}
                            sx={{
                                bgcolor: "#9C27B0",
                                "&:hover": { bgcolor: "#7B1FA2" },
                                minWidth: '100px'
                            }}
                        >
                            {pushingQuestion ? 'Pushing...' : 'Push'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box> 
    );
};

export default AdminRequests;